import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSnapshot } from 'valtio';

import getAssetByID from '@cartwhl/common/api/getAssetByID';

import { noticesStore, userStore } from 'stores/global';

import { applyAssetLibraryShareLinkAssets } from '../assetLibraryShareLinkResolve';
import {
  ASSET_LIBRARY_SHARE_ASSET_TYPE_PARAM,
  ASSET_LIBRARY_SHARE_GROUP_PARAM,
  ASSET_LIBRARY_SHARE_PREVIEW_PARAM,
  clearAssetLibraryShareLinkParams,
  getAssetLibraryShareLinkResolveId,
  hasAssetLibraryShareLinkParams,
} from '../assetLibraryShareLink';
import { useStore } from '../assetLibraryStore';

/** Opens a library asset preview or group detail from share-link URL params. */
export function useAssetLibraryShareLinkFromUrl(enabled: boolean) {
  const store = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiKey: apiKeyState, userInfo } = useSnapshot(userStore.state);
  const apiKey = apiKeyState.data?.apiKey;
  const requestedByUser = userInfo.userID;
  const processedLinkKeyRef = useRef<string | null>(null);
  const wasPreviewOpenRef = useRef(false);

  const previewId = searchParams.get(ASSET_LIBRARY_SHARE_PREVIEW_PARAM);
  const groupId = searchParams.get(ASSET_LIBRARY_SHARE_GROUP_PARAM);
  const assetTypeParam = searchParams.get(ASSET_LIBRARY_SHARE_ASSET_TYPE_PARAM);
  const linkKey = `${previewId ?? ''}|${groupId ?? ''}|${assetTypeParam ?? ''}`;

  const isPreviewOpen =
    store.state.mediaPreview.open ||
    Boolean(store.state.jobPreview.jobId) ||
    Boolean(store.state.selectedGroup);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (wasPreviewOpenRef.current && !isPreviewOpen) {
      if (hasAssetLibraryShareLinkParams(searchParams)) {
        setSearchParams(clearAssetLibraryShareLinkParams(searchParams), {
          replace: true,
        });
        processedLinkKeyRef.current = null;
      }
    }

    wasPreviewOpenRef.current = isPreviewOpen;
  }, [enabled, isPreviewOpen, searchParams, setSearchParams]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!previewId && !groupId) {
      processedLinkKeyRef.current = null;
      return;
    }

    if (processedLinkKeyRef.current === linkKey) {
      return;
    }

    if (!apiKey) {
      return;
    }

    const resolveId = getAssetLibraryShareLinkResolveId(previewId, groupId);
    if (!resolveId) {
      return;
    }

    const controller = new AbortController();

    void (async () => {
      const { data, error } = await getAssetByID(
        apiKey,
        resolveId,
        requestedByUser ? { requestedByUser } : {},
        controller.signal,
      );

      if (controller.signal.aborted) {
        return;
      }

      if (error || !data?.assets?.length) {
        processedLinkKeyRef.current = linkKey;
        noticesStore.error('Unable to find this asset.');
        setSearchParams(clearAssetLibraryShareLinkParams(searchParams), {
          replace: true,
        });
        return;
      }

      const opened = applyAssetLibraryShareLinkAssets(
        store,
        navigate,
        data.assets,
        { previewId, groupId, assetTypeParam },
      );

      if (!opened) {
        processedLinkKeyRef.current = linkKey;
        noticesStore.error('Unable to find this asset.');
        setSearchParams(clearAssetLibraryShareLinkParams(searchParams), {
          replace: true,
        });
        return;
      }

      processedLinkKeyRef.current = linkKey;
    })();

    return () => {
      controller.abort();
    };
  }, [
    apiKey,
    assetTypeParam,
    enabled,
    groupId,
    linkKey,
    navigate,
    previewId,
    requestedByUser,
    searchParams,
    setSearchParams,
    store,
  ]);
}
